import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { IconButton } from '@mui/material';
import Explainer from '../../components/Explainer';
import InfoTip from '../../components/InfoTip';
import EmailPreviewDialog from '../../components/EmailPreviewDialog';
import { exportToExcel } from '../../helpers/exportFuncs';

interface ScoreRow {
  send_id: number;
  is_non_opener: number;
  esp_campaign_id: string | null;
  client_code: string;
  campaign_id: number;
  mailing_number: number | null;
  mailing_type: string | null;
  type_seq_number: number | null;
  sent_at: string | null;
  recipients_norm: number | null;
  registrations: number;
  reg_per_1k: string | number | null;
  unsubscribes: number | null;
  unsub_rate: string | number | null;
  baseline_reg_per_1k: string | number | null;
  baseline_n: number | null;
  score: string | number | null;
  open_index: string | number | null;
  ctor_index: string | number | null;
  reg_per_click_index: string | number | null;
  is_provisional: number;
  is_inactive_send: number;
  scanner_suspect: number;
  stats_excluded: number;
  position_factor: string | number | null;
  regime_label: string | null;
  regime_start: string | null;
  campaign_name: string | null;
  webinar_title: string | null;
  subject: string | null;
}

interface CampaignRollup {
  campaign_id: number;
  campaign_name: string | null;
  webinar_title: string | null;
  first_sent_at: string | null;
  last_sent_at: string | null;
  sends: number;
  scored_sends: number;
  recipients: number | null;
  registrations: number | null;
  unsubscribes: number | null;
  unsub_rate: string | number | null;
  campaign_score: number | null;
  has_provisional: number;
}

interface CohortSendRow {
  sendId: number;
  isNonOpener: number;
  recipients: { cohort: number; older: number; unknown: number };
  registrations: { cohort: number; older: number; unknown: number };
  cohortRegPer1k: number | null;
  olderRegPer1k: number | null;
}

interface CohortKpi {
  days: number;
  ageSource: string;
  coveredSends: number;
  uncoveredSends: Array<{ sendId: number; sentAt: string }>;
  totals: {
    recipients: { cohort: number; older: number; unknown: number };
    registrations: { cohort: number; older: number; unknown: number };
    ageCoverage: number | null;
    cohortRegPer1k: number | null;
    olderRegPer1k: number | null;
  };
  sends: CohortSendRow[];
}

const EXPLAINER_SECTIONS = [
  {
    label: 'Datengrundlage',
    body: (
      <>
        Jeder Webinar-Mailing-Versand mit seinen zugeordneten Registrierungen — dieselbe Zuordnung
        wie im Auswertung-Tab, also echte Anmeldungen über das Webinar-Formular.
        <br />
        <br />
        <strong>Warum „EXT“ statt BG/BE/SW/TT einzeln?</strong> Seit dem VNR-Dubletten-Umbau (Mai
        2026) gehen diese vier Mailings an dieselbe kleine Zielgruppe — Empfänger, die sowohl bei
        GeVestor als auch bei Investor gelistet sind, plus bisherige Käufer — nur eben auf vier
        Marken verteilt. Ein einzelner Versand hat dann oft nur 20–300 Empfänger; ob dabei 0 oder 2
        Anmeldungen herauskommen, ist Zufall. Zusammengelegt sind es ~500 Empfänger pro Mailing —
        genug für eine belastbare Bewertung. Beispiel: am 26.07. gingen die vier HU1-Mails an
        zusammen 828 Empfänger und holten 18 Anmeldungen — als EIN „EXT“-Send bewertet.
        <br />
        <br />
        DNV ist standardmäßig ausgeblendet (bekommt seit Juli keine Webinar-Mailings mehr), über den
        Client-Filter aber einsehbar. Die Neuabonnenten-Kennzahl nutzt zusätzlich die
        Empfängerlisten der Finanztrends-Versände.
      </>
    ),
  },
  {
    label: 'Berechnung',
    body: (
      <>
        <strong>Score = 100 × Reg/1k ÷ Erwartung (n).</strong> Beide Zahlen stehen in der Tabelle —
        jeder Score lässt sich an der Zeile nachrechnen.
        <br />
        <br />
        Die <strong>Erwartung</strong> beantwortet: „Was hätte ein durchschnittliches Mailing an
        genau dieser Stelle gebracht?“ Sie kommt aus den letzten bis zu 10 vergleichbaren Versänden
        (gleicher Client, gleicher Mailing-Typ, gleiche Audience-Ära) und berücksichtigt die
        Position in der Serie: eine dritte Heat-Up-Mail bringt naturgemäß nur etwa halb so viele
        Anmeldungen wie die erste — ihre Erwartung ist entsprechend niedriger angesetzt.
        <br />
        <br />
        <strong>Beispiel durchgerechnet:</strong> Eine FT-HU3 holt 2,4 Registrierungen pro 1.000
        Empfänger. Die letzten vergleichbaren FT-Heat-Ups lagen bei 1,5 — für eine DRITTE Mail sind
        davon aber nur ~0,75 zu erwarten. 2,4 ÷ 0,75 ≈ 3,2 → <strong>Score 320</strong>: gut
        dreimal so viele Anmeldungen, wie an dieser Stelle üblich. 100 = genau die Erwartung
        getroffen, 200 = doppelt so gut, 50 = halb so gut.
      </>
    ),
  },
  {
    label: 'Aussage',
    body: (
      <>
        Der Score vergleicht eine Copy nur mit ihren Zeitgenossen. Audience-Umbauten
        (VNR-Dubletten-Gate, DNV-Migration, VNR-Ende, Listenwechsel) verfälschen ihn nicht, denn
        verglichen wird immer nur innerhalb derselben Ära („Regime“-Spalte). So lässt sich
        März-Copy fair gegen Juli-Copy stellen: entscheidend ist, wie stark ein Send seine eigene
        Vergleichsgruppe geschlagen hat — nicht die absolute Anmeldezahl, die von der Audience
        abhängt.
        <br />
        <br />
        <strong>Lesart für die Praxis:</strong> <strong>Grün (≥115)</strong> = klar besser als
        erwartet → Betreff/Aufbau als Vorlage für künftige Copy nutzen.{' '}
        <strong>Grau (85–115)</strong> = im normalen Rahmen. <strong>Rot (&lt;85)</strong> = klar
        schwächer → Muster dieser Copy künftig meiden. Über die Teil-Indizes sieht man, WO es lag —
        Beispiel: Öffnungs-Index 130 bei CTOR-Index 80 heißt „Betreff zog stark, der Inhalt hat das
        Versprechen nicht eingelöst“.
      </>
    ),
  },
  {
    label: 'Wichtig',
    body: (
      <>
        Öffnungs- und Klick-Indizes sind nur Diagnose: Apple-Mail lädt Mails automatisch im
        Hintergrund (zählt als „Öffnung“, ohne dass jemand liest), und Sicherheits-Scanner von
        Firmen-Postfächern klicken Links automatisch an. Registrierungen (echte
        Formular-Anmeldungen) macht kein Bot — deshalb tragen sie den Haupt-Score.
        <br />
        <br />
        Sends unter 100 Empfängern erhalten keinen Score („–“). Unter 5 Registrierungen oder mit
        wenigen Vergleichs-Sends gilt der Score als <strong>provisorisch</strong> („?“ am Score,
        „prov.“-Flag): als Tendenz brauchbar, als Ranking-Beweis nicht — Beispiel: 4 Anmeldungen
        statt erwarteter 2 ergibt Score ~200, kann aber schlicht Glück gewesen sein.
      </>
    ),
    highlight: true,
  },
];

// Header definitions with Marketing-facing tooltips — one array per table so
// header and body cells can't drift apart silently. `key` = sort field on the
// prepared row objects (undefined = column not sortable).
interface ColumnDef {
  label: string;
  tip: string;
  key?: string;
}

const ROLLUP_COLUMNS: ColumnDef[] = [
  { label: 'Kampagne', tip: 'Mailing-Kampagne (eine pro Webinar-Termin).', key: 'campaign_name' },
  { label: 'Webinar', tip: 'Zugehöriges Webinar.', key: 'webinar_title' },
  { label: 'Zuletzt', tip: 'Datum des letzten Versands dieser Kampagne.', key: 'last_sent_at' },
  {
    label: 'Sends',
    tip: 'Bewertete / alle Versände der Kampagne. BG/BE/SW/TT zählen zusammengefasst als ein EXT-Send.',
    key: 'sends',
  },
  {
    label: 'Empf.',
    tip: 'Empfänger über alle Versände der Kampagne summiert.',
    key: 'recipients',
  },
  {
    label: 'Reg.',
    tip: 'Webinar-Registrierungen, die den Versänden dieser Kampagne zugeordnet sind.',
    key: 'registrations',
  },
  {
    label: 'Abm.%',
    tip:
      'Abmelderate über alle Versände der Kampagne: Abmeldungen ÷ Empfänger (Rohwert, ohne ' +
      'Samstags-Normalisierung). Zählt jede Abmeldung nach dem Send — auch die Ein-Klick-Abmeldung ' +
      'im Mail-Programm. Typisch 0,1–0,3 %; deutlich mehr = die Serie nervt die Liste.',
    key: 'unsub_rate',
  },
  {
    label: 'Score',
    tip:
      'Kampagnen-Score: gewichteter Schnitt der Send-Scores (nach Empfängerzahl gewichtet, ' +
      'positions-korrigiert). 100 = Erwartung getroffen. „?" = der Score ruht ÜBERWIEGEND auf ' +
      'provisorischen Sends (mehr als die Hälfte des Gewichts) — einzelne kleine provisorische ' +
      'Sends lösen kein „?" mehr aus. ' +
      'Beispiel: 142 = die Kampagnen-Copy hat insgesamt 42 % mehr Anmeldungen geholt als üblich.',
    key: 'campaign_score',
  },
  {
    label: 'Aktionen',
    tip: 'Original-Kampagne im Editor öffnen (neuer Tab).',
  },
];

const SENDS_COLUMNS: ColumnDef[] = [
  { label: 'Datum', tip: 'Versanddatum.', key: 'sent_at' },
  {
    label: 'Client',
    tip:
      'FT = Finanztrends (Iterable). EXT = externe Publisher BG/BE/SW/TT als ein Send zusammengefasst ' +
      '(gleiche VNR-Dubletten-Zielgruppe). KME = Kapitalmarktexperten. DNV nur über den Filter sichtbar.',
    key: 'client_code',
  },
  {
    label: 'Typ',
    tip:
      'Position in der Serie: HU1/HU2/HU3 = Heat-Up-Mail 1–3, RL = Redlink (Verkaufs-Mail). ' +
      'Zusatz „NO" = Non-Opener-Nachversand an Nicht-Öffner.',
    key: 'typLabel',
  },
  { label: 'Betreff', tip: 'Betreffzeile der Mail. Hover zeigt Webinar und Kampagne.', key: 'subject' },
  {
    label: 'Regime',
    tip:
      'Audience-Ära zum Versandzeitpunkt (z.B. seit dem VNR-Dubletten-Gate oder dem Listenwechsel). ' +
      'Scores werden nur innerhalb desselben Regimes verglichen — deshalb bleiben sie über ' +
      'Audience-Umbauten hinweg fair. Beispiel: ein EXT-Send von April (volle Listen) wird nie ' +
      'gegen einen von Juli (nur VNR-Dubletten) gemessen.',
    key: 'regime_label',
  },
  {
    label: 'Empf.',
    tip:
      'Empfänger des Sends. Samstags-Sends mit Inaktiven zeigen den normalisierten Wert ' +
      '(Durchschnitt normaler Sends), damit die Rate vergleichbar bleibt.',
    key: 'recipients_norm',
  },
  { label: 'Reg.', tip: 'Webinar-Registrierungen, die diesem Send zugeordnet sind.', key: 'registrations' },
  {
    label: 'Reg/1k',
    tip:
      'Registrierungen pro 1.000 Empfänger — die Rohleistung des Sends. Beispiel: 86 Anmeldungen ' +
      'bei 35.716 Empfängern = 2,41.',
    key: 'reg_per_1k',
  },
  {
    label: 'Reg/1k neu',
    tip:
      'Registrierungen pro 1.000 NEU-Abonnenten dieses Sends — nur Empfänger, die zum ' +
      'Versandzeitpunkt höchstens so lange dabei waren wie im Fenster der Karte oben gewählt ' +
      '(Standard ≤14 Tage; Alters-Quelle ebenfalls von dort). Nur für Finanztrends verfügbar — ' +
      'nur da kennen wir die Empfänger einzeln. Hover zeigt die Rohzahlen. Achtung: kleine ' +
      'Gruppe pro Send (~500–1.500 Empfänger) — als Tendenz lesen, nicht als Beweis.',
    key: 'cohortRegPer1k',
  },
  {
    label: 'Erwartung (n)',
    tip:
      'Was ein durchschnittliches Mailing an genau dieser Stelle gebracht hätte (in Reg/1k): Schnitt ' +
      'der letzten vergleichbaren Sends, korrigiert um die Position in der Serie. n = Anzahl der ' +
      'Vergleichs-Sends. Es gilt sichtbar: Score = 100 × Reg/1k ÷ Erwartung. Beispiel: Reg/1k 2,41 ' +
      'bei Erwartung 0,75 → Score 320.',
    key: 'expected',
  },
  {
    label: 'Score',
    tip:
      'Haupt-Score der Copy: 100 = Erwartung getroffen, 200 = doppelt so gut, 50 = halb so gut. ' +
      'Grün ≥115, Rot <85. „?" = provisorisch (wenig Registrierungen oder wenig Vergleichs-Sends). ' +
      '„–" = kein Score (unter 100 Empfänger).',
    key: 'score',
  },
  {
    label: 'Öffn.-Idx',
    tip:
      'Öffnungsrate im Vergleich zu den Vergleichs-Sends (100 = normal). Diagnose für die ' +
      'BETREFFZEILE: 130 = Betreff zog 30 % mehr Öffnungen als üblich. Vorsicht: Apple-Mail lädt ' +
      'Mails automatisch und bläht Öffnungen auf.',
    key: 'open_index',
  },
  {
    label: 'CTOR-Idx',
    tip:
      'Klicks pro Öffnung im Vergleich (100 = normal). Diagnose für den MAIL-INHALT: hat der Text ' +
      'die Öffner zum Klick bewegt? Sicherheits-Scanner klicken mit — daher mit Vorsicht lesen.',
    key: 'ctor_index',
  },
  {
    label: 'Reg/Klick-Idx',
    tip:
      'Registrierungen pro Klick im Vergleich (100 = normal). Zeigt, wie gut Copy-Versprechen und ' +
      'Anmeldeseite zusammenpassen — der Bot-resistenteste der drei Teil-Indizes.',
    key: 'reg_per_click_index',
  },
  {
    label: 'Abm.%',
    tip:
      'Abmelderate dieses Sends: Abmeldungen ÷ Empfänger (Rohwert). Die Kosten-Seite der Copy — ' +
      'eine aggressive Betreffzeile kann Registrierungen UND Abmeldungen treiben. Typisch ' +
      '0,1–0,3 %. Hover zeigt die absolute Zahl.',
    key: 'unsub_rate',
  },
  {
    label: 'Flags',
    tip:
      'prov. = provisorischer Score (wenig Daten) · Sa = Samstags-Send inkl. Inaktive ' +
      '(normalisiert) · ⚠ = Scanner-Verdacht bei den Klicks · Ausfall = manuell von der ' +
      'Bewertung ausgeschlossen (z.B. technischer Vorfall).',
  },
  {
    label: 'Aktionen',
    tip: 'Auge = Copy-Vorschau der Mail · Pfeil = Original-Kampagne im Editor öffnen (neuer Tab).',
  },
];

type SortOrder = 'asc' | 'desc';

// Generic comparator: numbers numerically, strings per de-locale, null/undefined
// always LAST regardless of direction (a missing score must not win a ranking).
function compareValues(a: unknown, b: unknown, order: SortOrder): number {
  const aEmpty = a === null || a === undefined || a === '';
  const bEmpty = b === null || b === undefined || b === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  const dir = order === 'asc' ? 1 : -1;
  const na = Number(a);
  const nb = Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb)) return (na - nb) * dir;
  return String(a).localeCompare(String(b), 'de') * dir;
}

function sortRows<T extends Record<string, unknown>>(
  rows: T[],
  orderBy: string,
  order: SortOrder,
): T[] {
  return [...rows].sort((a, b) => compareValues(a[orderBy], b[orderBy], order));
}

// null/undefined mean "no value" — Number(null) is 0, which would render a
// misleading 0 instead of a dash.
const fmtNum = (v: unknown, decimals = 0): string => {
  if (v === null || v === undefined || v === '') return '–';
  const n = Number(v);
  if (!Number.isFinite(n)) return '–';
  return n.toLocaleString('de-DE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const scoreChip = (score: unknown, provisional: boolean) => {
  if (score === null || score === undefined || score === '') {
    return <Chip size="small" label="–" sx={{ backgroundColor: '#e0e0e0' }} />;
  }
  const n = Number(score);
  if (!Number.isFinite(n)) return <Chip size="small" label="–" sx={{ backgroundColor: '#e0e0e0' }} />;
  const bg = n >= 115 ? '#4cceac' : n < 85 ? '#e2726e' : '#c2c2c2';
  return (
    <Chip
      size="small"
      label={`${fmtNum(n, 0)}${provisional ? ' ?' : ''}`}
      sx={{ backgroundColor: bg, color: '#141414', fontWeight: 'bold' }}
    />
  );
};

const CopyScoreTab: React.FC = () => {
  const [clientCode, setClientCode] = useState('');
  const [mailingType, setMailingType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [ageSource, setAgeSource] = useState<'lfa_global' | 'iterable'>('lfa_global');
  const [cohortDays, setCohortDays] = useState(14);

  const [scores, setScores] = useState<ScoreRow[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignRollup[]>([]);
  const [cohort, setCohort] = useState<CohortKpi | null>(null);
  const [loading, setLoading] = useState(false);
  const [cohortLoading, setCohortLoading] = useState(false);

  const filterQs = useMemo(() => {
    const p = new URLSearchParams();
    if (clientCode) p.set('client_code', clientCode);
    if (mailingType) p.set('mailing_type', mailingType);
    if (startDate) p.set('startDate', startDate);
    if (endDate) p.set('endDate', endDate);
    return p.toString();
  }, [clientCode, mailingType, startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [scoresRes, campaignsRes] = await Promise.all([
          fetch(`/api/webinar-mailings/copy-scores/?${filterQs}`).then((r) => r.json()),
          fetch(`/api/webinar-mailings/copy-scores/campaigns?${filterQs}`).then((r) => r.json()),
        ]);
        if (cancelled) return;
        setScores(scoresRes.success ? scoresRes.data : []);
        setCampaigns(campaignsRes.success ? campaignsRes.data : []);
      } catch (err) {
        if (!cancelled) console.error('Copy-Score fetch failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filterQs]);

  useEffect(() => {
    let cancelled = false;
    setCohortLoading(true);
    (async () => {
      try {
        const p = new URLSearchParams();
        p.set('days', String(cohortDays));
        p.set('age_source', ageSource);
        if (startDate) p.set('startDate', startDate);
        if (endDate) p.set('endDate', endDate);
        const res = await fetch(
          `/api/webinar-mailings/copy-scores/new-subscriber-kpi?${p.toString()}`,
        ).then((r) => r.json());
        if (!cancelled) setCohort(res.success ? res.data : null);
      } catch (err) {
        if (!cancelled) console.error('Cohort KPI fetch failed:', err);
      } finally {
        if (!cancelled) setCohortLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ageSource, cohortDays, startDate, endDate]);

  const cohortRatio =
    cohort?.totals.cohortRegPer1k && cohort?.totals.olderRegPer1k
      ? cohort.totals.cohortRegPer1k / cohort.totals.olderRegPer1k
      : null;

  // Sorting state per table.
  const [sendsOrderBy, setSendsOrderBy] = useState('sent_at');
  const [sendsOrder, setSendsOrder] = useState<SortOrder>('desc');
  const [rollupOrderBy, setRollupOrderBy] = useState('last_sent_at');
  const [rollupOrder, setRollupOrder] = useState<SortOrder>('desc');

  // Send-view preview modal: fetches the ?rendered=1 variant of the preview
  // endpoint — placeholders resolved + client wrapper template applied, i.e.
  // the mail as it actually went out for the selected client.
  const [previewRow, setPreviewRow] = useState<(ScoreRow & { typLabel: string }) | null>(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewClient, setPreviewClient] = useState('ft');
  const [previewRenderNote, setPreviewRenderNote] = useState<string | null>(null);

  const previewClientsFor = (row: ScoreRow): { code: string; name: string }[] => {
    if (row.client_code === 'ext') {
      return [
        { code: 'bg', name: 'Börse-Global' },
        { code: 'be', name: 'Börse-Express' },
        { code: 'sw', name: 'Stock-World' },
        { code: 'tt', name: 'Trading-Treff' },
      ];
    }
    if (row.client_code === 'ft') return [{ code: 'ft', name: 'Finanztrends' }];
    return [{ code: row.client_code, name: row.client_code.toUpperCase() }];
  };

  const fetchPreview = async (row: ScoreRow & { typLabel: string }, clientCode: string) => {
    setPreviewLoading(true);
    try {
      const res = await fetch(
        `/api/webinar-mailings/campaigns/${row.campaign_id}/preview/${row.mailing_number}?rendered=1&client_code=${clientCode}`,
      ).then((r) => r.json());
      if (res.success && res.data) {
        setPreviewHtml(res.data.html_body || '');
        setPreviewSubject(res.data.subject || row.subject || '');
        setPreviewRenderNote(
          res.data.rendered
            ? null
            : `Versand-Ansicht nicht verfügbar (${res.data.render_error || 'unbekannt'}) — zeige Roh-Copy.`,
        );
      } else {
        setPreviewHtml('<p>Vorschau konnte nicht geladen werden.</p>');
        setPreviewRenderNote(null);
      }
    } catch (err) {
      console.error('Copy preview fetch failed:', err);
      setPreviewHtml('<p>Vorschau konnte nicht geladen werden.</p>');
      setPreviewRenderNote(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const openPreview = async (row: ScoreRow & { typLabel: string }) => {
    const initialClient = previewClientsFor(row)[0].code;
    setPreviewRow(row);
    setPreviewClient(initialClient);
    setPreviewHtml('');
    setPreviewSubject('');
    await fetchPreview(row, initialClient);
  };

  const handlePreviewClientChange = (clientCode: string) => {
    setPreviewClient(clientCode);
    if (previewRow) fetchPreview(previewRow, clientCode);
  };

  const openCampaignEditor = (campaignId: number) => {
    window.open(`/webinar-mailings?tab=editor&id=${campaignId}`, '_blank');
  };

  const handleSort = (
    key: string,
    orderBy: string,
    order: SortOrder,
    setOrderBy: (k: string) => void,
    setOrder: (o: SortOrder) => void,
  ) => {
    if (orderBy === key) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(key);
      setOrder('desc');
    }
  };

  // Prepared rows: computed sort/display fields (Erwartung = Basis × Positionsfaktor,
  // so Score = 100 × Reg/1k ÷ Erwartung is verifiable per row) + the per-send
  // ≤N-Tage-Neuabonnenten rate joined from the cohort endpoint (FT only).
  const sendRows = useMemo(() => {
    const cohortBySend = new Map<string, CohortSendRow>();
    for (const cs of cohort?.sends || []) {
      cohortBySend.set(`${cs.isNonOpener}:${cs.sendId}`, cs);
    }
    return scores.map((s) => {
      const cs = cohortBySend.get(`${s.is_non_opener}:${s.send_id}`);
      return {
        ...s,
        typLabel: `${s.mailing_type === 'redlink' ? 'RL' : 'HU'}${
          s.type_seq_number || s.mailing_number || ''
        }${s.is_non_opener ? ' NO' : ''}`,
        expected:
          s.baseline_reg_per_1k !== null && s.position_factor !== null
            ? Number(s.baseline_reg_per_1k) * Number(s.position_factor)
            : null,
        cohortRegPer1k: cs?.cohortRegPer1k ?? null,
        cohortRegs: cs?.registrations.cohort ?? null,
        cohortRecipients: cs?.recipients.cohort ?? null,
      };
    });
  }, [scores, cohort]);
  const sortedSendRows = useMemo(
    () => sortRows(sendRows as unknown as Record<string, unknown>[], sendsOrderBy, sendsOrder),
    [sendRows, sendsOrderBy, sendsOrder],
  ) as unknown as typeof sendRows;
  const sortedCampaigns = useMemo(
    () =>
      sortRows(campaigns as unknown as Record<string, unknown>[], rollupOrderBy, rollupOrder),
    [campaigns, rollupOrderBy, rollupOrder],
  ) as unknown as CampaignRollup[];

  const exportSends = () => {
    exportToExcel(
      sortedSendRows.map((s) => ({
        Datum: s.sent_at ? String(s.sent_at).slice(0, 10) : '',
        Client: s.client_code === 'ext' ? 'EXT (BG/BE/SW/TT)' : s.client_code.toUpperCase(),
        Typ: s.typLabel,
        Betreff: s.subject || '',
        Webinar: s.webinar_title || '',
        Kampagne: s.campaign_name || '',
        Regime: s.regime_label || '',
        Empfaenger: Number(s.recipients_norm) || 0,
        Registrierungen: Number(s.registrations) || 0,
        RegPro1k: s.reg_per_1k !== null ? Number(s.reg_per_1k) : null,
        RegPro1kNeuAbonnenten: s.cohortRegPer1k !== null ? Math.round(s.cohortRegPer1k * 100) / 100 : null,
        NeuAbonnentenRegs: s.cohortRegs,
        NeuAbonnentenEmpfaenger: s.cohortRecipients,
        Abmeldungen: s.unsubscribes,
        AbmeldeRateProzent: s.unsub_rate !== null ? Number(s.unsub_rate) : null,
        Erwartung: s.expected !== null ? Math.round(s.expected * 10000) / 10000 : null,
        VergleichsSends: s.baseline_n,
        Score: s.score !== null ? Number(s.score) : null,
        OeffnungsIndex: s.open_index !== null ? Number(s.open_index) : null,
        CtorIndex: s.ctor_index !== null ? Number(s.ctor_index) : null,
        RegProKlickIndex: s.reg_per_click_index !== null ? Number(s.reg_per_click_index) : null,
        Flags: [
          s.is_provisional ? 'provisorisch' : '',
          s.is_inactive_send ? 'Samstag-inkl-Inaktive' : '',
          s.scanner_suspect ? 'Scanner-Verdacht' : '',
          s.stats_excluded ? 'Ausfall' : '',
        ]
          .filter(Boolean)
          .join(', '),
      })),
      'webinar-copy-score-sends',
    );
  };

  const exportCampaigns = () => {
    exportToExcel(
      sortedCampaigns.map((c) => ({
        Kampagne: c.campaign_name || String(c.campaign_id),
        Webinar: c.webinar_title || '',
        ErsterVersand: c.first_sent_at ? String(c.first_sent_at).slice(0, 10) : '',
        LetzterVersand: c.last_sent_at ? String(c.last_sent_at).slice(0, 10) : '',
        SendsBewertet: Number(c.scored_sends) || 0,
        SendsGesamt: Number(c.sends) || 0,
        Empfaenger: Number(c.recipients) || 0,
        Registrierungen: Number(c.registrations) || 0,
        Abmeldungen: c.unsubscribes,
        AbmeldeRateProzent: c.unsub_rate !== null ? Number(c.unsub_rate) : null,
        KampagnenScore: c.campaign_score !== null ? Number(c.campaign_score) : null,
        EnthaeltProvisorische: c.has_provisional ? 'ja' : 'nein',
      })),
      'webinar-copy-score-kampagnen',
    );
  };

  const renderHeader = (
    columns: ColumnDef[],
    orderBy: string,
    order: SortOrder,
    setOrderBy: (k: string) => void,
    setOrder: (o: SortOrder) => void,
  ) => (
    <TableRow>
      {columns.map((c) => (
        <TableCell
          key={c.label}
          sortDirection={c.key && orderBy === c.key ? order : false}
          sx={{
            backgroundColor: '#e0e0e0 !important',
            color: 'black',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
          }}
        >
          {c.key ? (
            <TableSortLabel
              active={orderBy === c.key}
              direction={orderBy === c.key ? order : 'desc'}
              onClick={() => handleSort(c.key as string, orderBy, order, setOrderBy, setOrder)}
            >
              {c.label}
            </TableSortLabel>
          ) : (
            c.label
          )}
          <InfoTip text={c.tip} />
        </TableCell>
      ))}
    </TableRow>
  );

  return (
    <Box>
      <Explainer title="Wie funktioniert der Copy-Score?" sections={EXPLAINER_SECTIONS} />

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Client</InputLabel>
          <Select value={clientCode} label="Client" onChange={(e) => setClientCode(e.target.value)}>
            <MenuItem value="">Alle (ohne DNV)</MenuItem>
            <MenuItem value="ft">FT</MenuItem>
            <MenuItem value="ext">EXTERNAL (BG/BE/SW/TT)</MenuItem>
            <MenuItem value="kme">KME</MenuItem>
            <MenuItem value="dnv">DNV</MenuItem>
            <MenuItem value="all">Alle inkl. DNV</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Mailing-Typ</InputLabel>
          <Select
            value={mailingType}
            label="Mailing-Typ"
            onChange={(e) => setMailingType(e.target.value)}
          >
            <MenuItem value="">Alle</MenuItem>
            <MenuItem value="heatup">Heat-Up</MenuItem>
            <MenuItem value="redlink">Redlink</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          type="date"
          label="Von"
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <TextField
          size="small"
          type="date"
          label="Bis"
          InputLabelProps={{ shrink: true }}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
      </Box>

      <Paper sx={{ p: 2, mb: 3, backgroundColor: '#ffffff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h5" sx={{ color: '#141414', fontWeight: 'bold' }}>
            Neuabonnenten-KPI (FT/Iterable)
            <InfoTip
              text={
                'Registrieren sich frische Abonnenten besser als der Bestand? Vergleicht die ' +
                'Registrierungsrate von Empfängern, die zum Versandzeitpunkt höchstens X Tage ' +
                'angemeldet waren, mit allen älteren. Nur Finanztrends — hier kennen wir je Send ' +
                'die einzelnen Empfänger. Alters-Quelle „Erst-DOI (global)" = erstes ' +
                'Anmeldedatum im gesamten Verlag (empfohlen); „Iterable-Anlage" = Anlagedatum des ' +
                'Profils (durch Importe verfälschbar).'
              }
            />
          </Typography>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Alters-Quelle</InputLabel>
            <Select
              value={ageSource}
              label="Alters-Quelle"
              onChange={(e) => setAgeSource(e.target.value as 'lfa_global' | 'iterable')}
            >
              <MenuItem value="lfa_global">Erst-DOI (global)</MenuItem>
              <MenuItem value="iterable">Iterable-Anlage</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Fenster</InputLabel>
            <Select
              value={cohortDays}
              label="Fenster"
              onChange={(e) => setCohortDays(Number(e.target.value))}
            >
              {[7, 14, 30].map((d) => (
                <MenuItem key={d} value={d}>{`≤ ${d} Tage`}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {cohortLoading && <CircularProgress size={20} />}
        </Box>
        {cohort ? (
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mt: 2 }}>
            <Box>
              <Typography variant="caption" sx={{ color: '#525252' }}>
                Neuabonnenten Reg/1k
                <InfoTip text="Registrierungen pro 1.000 Empfänger unter Abonnenten, die zum Versandzeitpunkt höchstens so viele Tage angemeldet waren wie im Fenster gewählt." />
              </Typography>
              <Typography variant="h4" sx={{ color: '#141414', fontWeight: 'bold' }}>
                {fmtNum(cohort.totals.cohortRegPer1k, 2)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#525252' }}>
                {fmtNum(cohort.totals.registrations.cohort)} Reg. /{' '}
                {fmtNum(cohort.totals.recipients.cohort)} Empf.
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#525252' }}>
                Bestand Reg/1k
                <InfoTip text="Registrierungen pro 1.000 Empfänger unter allen älteren Abonnenten — die Vergleichsgruppe zur Neuabonnenten-Rate." />
              </Typography>
              <Typography variant="h4" sx={{ color: '#141414', fontWeight: 'bold' }}>
                {fmtNum(cohort.totals.olderRegPer1k, 2)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#525252' }}>
                {fmtNum(cohort.totals.registrations.older)} Reg. /{' '}
                {fmtNum(cohort.totals.recipients.older)} Empf.
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#525252' }}>
                Faktor neu vs. Bestand
                <InfoTip text="Wie viel besser Neuabonnenten registrieren als der Bestand. 2× = doppelt so hohe Registrierungsrate in den ersten Tagen." />
              </Typography>
              <Typography variant="h4" sx={{ color: '#141414', fontWeight: 'bold' }}>
                {cohortRatio ? `${fmtNum(cohortRatio, 2)}×` : '–'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#525252' }}>
                Alters-Abdeckung
                <InfoTip text="Anteil der Empfänger mit bekanntem Anmeldedatum. Empfänger ohne Datum fließen in keine der beiden Raten ein — die Kennzahl rechnet nur mit der bekannten Gruppe." />
              </Typography>
              <Typography variant="h4" sx={{ color: '#141414', fontWeight: 'bold' }}>
                {cohort.totals.ageCoverage !== null
                  ? `${fmtNum(cohort.totals.ageCoverage * 100, 1)} %`
                  : '–'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#525252' }}>
                {fmtNum(cohort.coveredSends)} Sends mit Spiegel
                {cohort.uncoveredSends.length > 0 &&
                  ` · ${cohort.uncoveredSends.length} ohne (nicht enthalten)`}
              </Typography>
            </Box>
          </Box>
        ) : (
          !cohortLoading && (
            <Typography sx={{ mt: 2, color: '#525252' }}>
              Spiegel unvollständig — keine abgedeckten Sends im Zeitraum.
            </Typography>
          )
        )}
      </Paper>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Kampagnen-Rollup (positions-korrigiert, gewichtet)
          <InfoTip
            text={
              'Alle Sends einer Kampagne zu einem Gesamt-Score zusammengefasst. Positions-korrigiert: ' +
              'spätere Mails der Serie bringen naturgemäß weniger Registrierungen — das wird ' +
              'herausgerechnet, damit Kampagnen mit unterschiedlich vielen Mails fair vergleichbar sind.'
            }
          />
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={exportCampaigns}
          disabled={campaigns.length === 0}
        >
          Excel
        </Button>
      </Box>
      <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', maxHeight: 380, mb: 3 }}>
        <Table stickyHeader size="small">
          <TableHead>
            {renderHeader(ROLLUP_COLUMNS, rollupOrderBy, rollupOrder, setRollupOrderBy, setRollupOrder)}
          </TableHead>
          <TableBody>
            {sortedCampaigns.map((c) => (
              <TableRow key={c.campaign_id} hover>
                <TableCell sx={{ color: '#141414' }}>{c.campaign_name || c.campaign_id}</TableCell>
                <TableCell sx={{ color: '#141414' }}>{c.webinar_title || '–'}</TableCell>
                <TableCell sx={{ color: '#141414' }}>
                  {c.last_sent_at ? c.last_sent_at.slice(0, 10) : '–'}
                </TableCell>
                <TableCell sx={{ color: '#141414' }}>
                  {c.scored_sends}/{c.sends}
                </TableCell>
                <TableCell sx={{ color: '#141414' }}>{fmtNum(c.recipients)}</TableCell>
                <TableCell sx={{ color: '#141414' }}>{fmtNum(c.registrations)}</TableCell>
                <TableCell sx={{ color: '#141414' }}>
                  {c.unsub_rate !== null ? (
                    <Tooltip title={`${fmtNum(c.unsubscribes)} Abmeldungen`}>
                      <span>{fmtNum(c.unsub_rate, 2)} %</span>
                    </Tooltip>
                  ) : (
                    '–'
                  )}
                </TableCell>
                <TableCell>{scoreChip(c.campaign_score, !!c.has_provisional)}</TableCell>
                <TableCell>
                  <Tooltip title="Kampagne im Editor öffnen (neuer Tab)">
                    <IconButton size="small" onClick={() => openCampaignEditor(c.campaign_id)}>
                      <OpenInNewOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Einzelne Sends
          <InfoTip
            text={
              'Jede Zeile = ein Versand mit seinem Copy-Score. BG/BE/SW/TT erscheinen als ein ' +
              'gemeinsamer EXT-Send. Details zu jeder Spalte über das Info-Symbol im Tabellenkopf.'
            }
          />{' '}
          {loading && <CircularProgress size={16} sx={{ ml: 1 }} />}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={exportSends}
          disabled={sendRows.length === 0}
        >
          Excel
        </Button>
      </Box>
      <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', maxHeight: 600 }}>
        <Table stickyHeader size="small">
          <TableHead>
            {renderHeader(SENDS_COLUMNS, sendsOrderBy, sendsOrder, setSendsOrderBy, setSendsOrder)}
          </TableHead>
          <TableBody>
            {sortedSendRows.map((s) => (
              <TableRow key={`${s.is_non_opener}-${s.send_id}`} hover>
                <TableCell sx={{ color: '#141414', whiteSpace: 'nowrap' }}>
                  {s.sent_at ? String(s.sent_at).slice(0, 10) : '–'}
                </TableCell>
                <TableCell sx={{ color: '#141414' }}>
                  {s.client_code === 'ext' ? (
                    <Tooltip title="Gepoolte externe Publisher: BG/BE/SW/TT zusammengefasst (gleiche VNR-Dubletten-Audience, einzeln unter der Rausch-Grenze)">
                      <span>EXT</span>
                    </Tooltip>
                  ) : (
                    s.client_code.toUpperCase()
                  )}
                </TableCell>
                <TableCell sx={{ color: '#141414', whiteSpace: 'nowrap' }}>{s.typLabel}</TableCell>
                <TableCell sx={{ color: '#141414', maxWidth: 260 }}>
                  <Tooltip title={`${s.webinar_title || ''} — ${s.campaign_name || ''}`}>
                    <span>{(s.subject || '–').slice(0, 60)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ color: '#141414' }}>
                  <Tooltip title={`Regime seit ${s.regime_start ? String(s.regime_start).slice(0, 10) : '–'}`}>
                    <Chip size="small" label={s.regime_label || '–'} sx={{ backgroundColor: '#e0e0e0' }} />
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ color: '#141414' }}>{fmtNum(s.recipients_norm)}</TableCell>
                <TableCell sx={{ color: '#141414' }}>{fmtNum(s.registrations)}</TableCell>
                <TableCell sx={{ color: '#141414' }}>{fmtNum(s.reg_per_1k, 2)}</TableCell>
                <TableCell sx={{ color: '#141414' }}>
                  {s.cohortRegPer1k !== null ? (
                    <Tooltip
                      title={`${fmtNum(s.cohortRegs)} Reg. / ${fmtNum(s.cohortRecipients)} Neu-Abonnenten — bei der Gesamtrate des Sends wären ~${fmtNum(((Number(s.cohortRecipients) || 0) * (Number(s.reg_per_1k) || 0)) / 1000, 1)} Reg. zu erwarten. Bei so kleinen Gruppen ist auch 0 oft reiner Zufall.`}
                    >
                      <span
                        style={
                          // Grey out when even the expected count is below ~1 —
                          // the cohort is too small for this send to say anything.
                          ((Number(s.cohortRecipients) || 0) * (Number(s.reg_per_1k) || 0)) / 1000 < 1
                            ? { color: '#8a8a8a' }
                            : undefined
                        }
                      >
                        {fmtNum(s.cohortRegPer1k, 2)}
                      </span>
                    </Tooltip>
                  ) : (
                    '–'
                  )}
                </TableCell>
                <TableCell sx={{ color: '#141414' }}>
                  {fmtNum(s.expected, 2)} ({s.baseline_n ?? 0})
                </TableCell>
                <TableCell>{scoreChip(s.score, !!s.is_provisional)}</TableCell>
                <TableCell sx={{ color: '#141414' }}>{fmtNum(s.open_index)}</TableCell>
                <TableCell sx={{ color: '#141414' }}>{fmtNum(s.ctor_index)}</TableCell>
                <TableCell sx={{ color: '#141414' }}>{fmtNum(s.reg_per_click_index)}</TableCell>
                <TableCell sx={{ color: '#141414' }}>
                  {s.unsub_rate !== null ? (
                    <Tooltip title={`${fmtNum(s.unsubscribes)} Abmeldungen`}>
                      <span>{fmtNum(s.unsub_rate, 2)} %</span>
                    </Tooltip>
                  ) : (
                    '–'
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {!!s.is_provisional && (
                      <Tooltip title="Provisorisch: wenig Vergleichs-Sends oder unter 5 Registrierungen">
                        <Chip size="small" label="prov." sx={{ backgroundColor: '#e0e0e0' }} />
                      </Tooltip>
                    )}
                    {!!s.is_inactive_send && (
                      <Tooltip title="Samstags-Send mit Inaktiven (normalisierte Empfängerzahl)">
                        <Chip size="small" label="Sa" sx={{ backgroundColor: '#e0e0e0' }} />
                      </Tooltip>
                    )}
                    {!!s.scanner_suspect && (
                      <Tooltip title="Scanner-Verdacht: auffällig hohe Klickrate — Klick-Indizes mit Vorsicht">
                        <Chip size="small" label="⚠" sx={{ backgroundColor: '#e2726e' }} />
                      </Tooltip>
                    )}
                    {!!s.stats_excluded && (
                      <Tooltip title="Ausfall: manuell von der Baseline ausgeschlossen">
                        <Chip size="small" label="Ausfall" sx={{ backgroundColor: '#e2726e' }} />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Copy-Vorschau">
                    <IconButton size="small" onClick={() => openPreview(s)}>
                      <VisibilityOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Kampagne im Editor öffnen (neuer Tab)">
                    <IconButton size="small" onClick={() => openCampaignEditor(s.campaign_id)}>
                      <OpenInNewOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <EmailPreviewDialog
        open={previewRow !== null}
        onClose={() => setPreviewRow(null)}
        previewHtml={previewHtml}
        subject={previewSubject}
        clients={previewRow ? previewClientsFor(previewRow) : []}
        selectedClient={previewClient}
        onClientChange={handlePreviewClientChange}
        loading={previewLoading}
        title={
          previewRow
            ? `Versand-Ansicht — ${previewRow.typLabel} · ${previewRow.campaign_name || ''}`
            : 'Versand-Ansicht'
        }
        infoBox={
          previewRow ? (
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2">
                  {previewRow.webinar_title || ''} · versendet{' '}
                  {previewRow.sent_at ? String(previewRow.sent_at).slice(0, 10) : '–'}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<OpenInNewOutlinedIcon />}
                  onClick={() => openCampaignEditor(previewRow.campaign_id)}
                >
                  Original-Kampagne öffnen
                </Button>
                {previewRow.client_code === 'ft' && previewRow.esp_campaign_id && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<OpenInNewOutlinedIcon />}
                    onClick={() =>
                      window.open(
                        `https://app.eu.iterable.com/campaigns/${previewRow.esp_campaign_id}`,
                        '_blank',
                      )
                    }
                  >
                    In Iterable öffnen
                  </Button>
                )}
              </Box>
              {previewRenderNote && (
                <Typography variant="caption" sx={{ color: '#b23b3b', display: 'block', mt: 0.5 }}>
                  {previewRenderNote}
                </Typography>
              )}
            </Box>
          ) : undefined
        }
      />
    </Box>
  );
};

export default CopyScoreTab;
